import {
  ID,
  overdueSectionID,
  Timestamp,
  TranslateFunction,
  upcomingMonthPrefix,
  upcomingRestOfMonthPrefix,
  upcomingYearPrefix,
} from '@griply/types';
import { getDateSectionLabel, parseDateKey } from '@griply/utils';
import {
  endOfDay,
  endOfMonth,
  endOfYear,
  format,
  startOfDay,
  startOfMonth,
} from 'date-fns';

export enum SectionType {
  Day = 'day',
  RestOfMonth = 'rest-of-month',
  Month = 'month',
  Year = 'year',
  Overdue = 'overdue',
}

export type ParsedSection = {
  type: SectionType;
  date: Timestamp | undefined;
  startDate: Timestamp | undefined;
  endDate: Timestamp | undefined;
};

type SectionRange = (
  date: Timestamp,
) => Pick<ParsedSection, 'startDate' | 'endDate'>;

const sectionRanges: Record<SectionType, SectionRange> = {
  [SectionType.Overdue]: () => ({ startDate: undefined, endDate: undefined }),
  [SectionType.Day]: (date) => ({
    startDate: startOfDay(date),
    endDate: endOfDay(date),
  }),
  [SectionType.RestOfMonth]: (date) => ({
    startDate: startOfDay(date),
    endDate: endOfMonth(date),
  }),
  [SectionType.Month]: (date) => ({
    startDate: startOfMonth(date),
    endDate: endOfMonth(date),
  }),
  [SectionType.Year]: (date) => ({
    startDate: startOfMonth(date),
    endDate: endOfYear(date),
  }),
};

const prefixedTypes: [string, SectionType][] = [
  [upcomingRestOfMonthPrefix, SectionType.RestOfMonth],
  [upcomingMonthPrefix, SectionType.Month],
  [upcomingYearPrefix, SectionType.Year],
];

const parseSectionDate = (sectionId: ID) => {
  const prefixed = prefixedTypes.find(([prefix]) =>
    sectionId.startsWith(prefix),
  );
  if (!prefixed) {
    return { type: SectionType.Day, dateKey: sectionId };
  }
  const [prefix, type] = prefixed;
  return { type, dateKey: sectionId.slice(prefix.length) };
};

export const parseSectionId = (sectionId: ID): ParsedSection => {
  if (sectionId === overdueSectionID) {
    return {
      type: SectionType.Overdue,
      date: undefined,
      startDate: undefined,
      endDate: undefined,
    };
  }

  const { type, dateKey } = parseSectionDate(sectionId);
  const date = parseDateKey(dateKey);
  if (isNaN(date.getTime())) {
    return { type, date: undefined, startDate: undefined, endDate: undefined };
  }
  return { type, date, ...sectionRanges[type](date) };
};

export const getSectionLabel = (
  section: ParsedSection,
  today: Timestamp,
  t: TranslateFunction,
): string => {
  if (section.type === SectionType.Overdue) {
    return t('general.sections.overdue.title');
  }
  if (!section.date) {
    return '';
  }
  if (section.type === SectionType.Day) {
    const { primary, secondary } = getDateSectionLabel(section.date, today, t);
    return `${primary} ${secondary}`;
  }
  if (section.type === SectionType.RestOfMonth) {
    return t('general.labels.rest-of-month.title', {
      month: format(section.date, 'LLLL'),
    });
  }
  if (section.type === SectionType.Month) {
    return format(section.date, 'LLLL');
  }
  return format(section.date, 'yyyy');
};
